/*
 * Athena / DataAPI client — the single runtime data source that replaces the old
 * Allociné (AcGraphApi) and Webedia (DotProGraphExternal) clients.
 *
 * Requests no longer hit Athena directly from the browser: they POST to the
 * same-origin `/api/data-api` Netlify Function (see netlify/functions/data-api.ts and
 * the redirect in netlify.toml), which holds the API key server-side (`ATHENA_KEY`) and
 * forwards the query to Athena. The key is no longer shipped to the client.
 *
 * One endpoint serves theaters and showtimes/movies, so the old two-API split and
 * base64 ID bridging are gone — theater/movie IDs are used verbatim.
 *
 * Error contract: returns `null` on a missing/empty result (matching the old
 * `graphQuery`), so every caller must null-check / default to an empty result.
 */

// UKCA's affiliated-company id (the bare numeric id the old base64 GATSBY_API_ID
// decoded to). Used as `affiliationId` / `entityId.id` to scope to UKCA's circuit.
// Not a secret (embedded into query strings built in the browser), so it stays client-side.
export const AFFILIATION_ID =
  typeof process.env.GATSBY_API_AFFILIATION_ID !== 'undefined'
    ? process.env.GATSBY_API_AFFILIATION_ID
    : '';

// UKCA is GB-only (replaces the old `countries: [UNITED_KINGDOM]`).
export const COUNTRY = 'GB';

// Same-origin proxy that injects the API key server-side (netlify.toml redirects this
// to /.netlify/functions/data-api).
const REQUEST_URL = '/api/data-api';

export function athenaQuery(query: string, resultKey: string) {
  return fetch(REQUEST_URL, {
    body: JSON.stringify({ query }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
    .then((r) => r.json())
    .then((r) => {
      if (
        typeof r === 'undefined' ||
        r === null ||
        typeof r.data === 'undefined' ||
        r.data === null ||
        typeof r.data[resultKey] === 'undefined' ||
        r.data[resultKey] === null
      ) {
        return null;
      }
      return r;
    });
}

/*
 * Athena returns only latitude/longitude per theater (no computed distance like
 * the old APIs), so distance for the "N miles" UI is computed client-side.
 * ES5-only math (no `**`) to stay safe under the IE11 target.
 */
const EARTH_RADIUS_MILES = 3958.8;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const haversineMiles = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
};

// Athena geo filters (maxDistance/aroundCoords) are in kilometres; the CMS
// locationRadius is in miles. Round up so edge cinemas aren't clipped.
export const milesToKm = (miles: number): number => Math.ceil(miles * 1.60934);

// Athena returns theaters regardless of opening status (Allociné only returned open
// ones). Request `opening { status }` and drop permanently-closed theaters with this.
export const isTheaterOpen = (node: any): boolean =>
  !node.opening || node.opening.status !== 'DEFINITIVELY_CLOSED';

// Maps Athena screen `tags` (dotted, e.g. "Screen.Accessibility.AudioDescription")
// back to the legacy `screens[].accessibility` vocabulary the cinema UI matches
// against (getScreeningTypes, the screens list), so those stay unchanged.
const SCREEN_TAG_TO_ACCESSIBILITY: { [tag: string]: string } = {
  'Screen.Accessibility.Accessible': 'ACCESSIBLE',
  'Screen.Accessibility.AudioDescription': 'AUDIO_DESCRIPTION',
};

export const mapScreenTags = (tags: string[]): string[] => {
  const accessibility: string[] = [];
  (tags || []).forEach((tag) => {
    if (SCREEN_TAG_TO_ACCESSIBILITY[tag]) {
      accessibility.push(SCREEN_TAG_TO_ACCESSIBILITY[tag]);
    }
  });
  return accessibility;
};
